// 🔧 event_home.js
// ✅ Purpose: Public-facing landing page for a specific event, accessible by any user
// ✅ Features:
// - Uses color-block layout from landing_home.js
// - Gets eventId and event data using useEventContext()
// - Loads event settings (description, rules, etc.) from /settings/voting via session data
// - Shows event logo, name, and start date
// - Sections: Event Details, Tips, Swap Rules, Sponsors, Camping, Feedback, Footer

import { isEventPublic } from "../../utils/event_visibility";
import React, { useEffect, useMemo, useState } from 'react';
import {
    collection,
    addDoc,
    serverTimestamp,
    getDocs,
    getDoc,
    doc,
    query,
    getCountFromServer,
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { useEventContext } from '../../hooks/use_event_context';
import { useEventSettings } from '../../hooks/use_event_settings';
import FullPageSpinner from '../../components/FullPageSpinner';
import { logger } from '../../utils/logger';
import { showStatus } from "../../utils/status_helpers";
import { Link } from "react-router-dom";
import 'react-quill/dist/quill.snow.css';
import { useVotingStatus } from '../../hooks/use_voting_status';
import { getEventVotingType } from "../../utils/event_voting_type_helpers";
import SeoHead from "../../components/seo_head";
import { PUBLIC_PAGE_SEO } from "../../constants/marketing_pages";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";
import {
  useContent,
} from "../../content_engine";
import { getEntryRegistrationLabel } from "../../utils/entry_registration_label";
import { getEntryImageUrl } from "../../utils/demo_entry_assets";
import { loadEventResultProjection } from "../../services/event_results";
import { sanitizeHtml } from "../../utils/sanitize_html";
import PromotionSourceButton from "../../promotion/components/promotion_source_button";
import { arePromotionResultsPublished } from "../../promotion/constants/promotion_source_pages";

const DEFAULT_PARTICIPATION_AND_VOTING_TIPS = `
  <p><strong>Participation and Voting Tips</strong></p>
  <ul>
    <li>Review the event details, categories, and entries before voting.</li>
    <li>To vote, find an entry you would like to vote for and scan the QR code displayed with that entry.</li>
    <li>Log in or create an account if prompted.</li>
    <li>Review the entry details before submitting your vote.</li>
    <li>You can visit your ballot at any time to review your submitted votes.</li>
    <li>Voting may only be available during the official voting window for this event.</li>
  </ul>
`;

const EVENT_HOME_SECTION_ORDER = {
    before_event: [
        "event_details",
        "store",
        "volunteers",
        "sponsors",
        "auction",
        "vendors",
        "optional_1",
        "optional_2",
        "participation_tips",
    ],

    countdown_window: [
        "event_details",
        "store",
        "auction",
        "optional_1",
        "optional_2",
        "map",
        "schedule",
        "participation_tips",
        "sponsors",
        "vendors",
        "volunteers",
    ],

    event_day: [
        "map",
        "schedule",
        "participation_tips",
        "auction",
        "sponsors",
        "vendors",
        "event_details",
        "store",
        "optional_1",
        "optional_2",
        "volunteers",
    ],

    after_event: [
        "winners",
        "sponsors",
        "store",
        "auction",
        "vendors",
        "event_details",
        "volunteers",
        "map",
        "schedule",
        "participation_tips",
        "optional_1",
        "optional_2",
    ],
};

const EventHome = () => {

    const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
    } = useEventContext({ redirectIfNotFound: true });
    const { settings, loading } = useEventSettings(eventId || undefined);
    const {
    content: communicationContent,
    loading: communicationLoading,
    } = useContent({
    area: "event_home",
    });    
    console.log(
    "💬 Event Home Communication Content:",
    communicationContent
);
    const description = settings?.description || "";
    const participationTips =
        settings?.participationTips || DEFAULT_PARTICIPATION_AND_VOTING_TIPS;
    const optionalDetails1 = settings?.optionalDetails1 || "";
    const optionalDetails2 = settings?.optionalDetails2 || "";
    const feedbackNote = settings?.feedbackNote || "";
    const [status, setStatus] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [sponsors, setSponsors] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [storeProducts, setStoreProducts] = useState([]);
    const sponsorLevels = settings?.sponsorLevels || [];
    const [volunteerActivityCount, setVolunteerActivityCount] = useState(0);
    const [canManageEvent, setCanManageEvent] = useState(false);
    const [user] = useAuthState(auth);
    const { isVotingOpen } = useVotingStatus(eventId);
    const [categoryCount, setCategoryCount] = useState(0);
    const [entryCount, setEntryCount] = useState(0);
    const [entries, setEntries] = useState([]);
    const [categories, setCategories] = useState([]);
    const [resultProjection, setResultProjection] = useState(null);
    const [auctionItemCount, setAuctionItemCount] = useState(0);
    const [volunteerContactPromoter, setVolunteerContactPromoter] = useState("");
    const [volunteerGoodies, setVolunteerGoodies] = useState("");
    const [futureVolunteerEmail, setFutureVolunteerEmail] = useState("");
    const [futureVolunteerSubmitting, setFutureVolunteerSubmitting] = useState(false);    
    const [scheduleItems, setScheduleItems] = useState([]);
    const [setupProgressExpanded, setSetupProgressExpanded] = useState(true);
    const sponsorAwardResultsShowAt = settings?.sponsorAwardResultsShowAt || "";
    const showWinnerResultsAt = settings?.showWinnerResultsAt || "";
    const winnerPlaceLimit = Number(settings?.winnerPlaceLimit || 3);
    const showWinnerVoteCounts = settings?.showWinnerVoteCounts !== false;
    const [eventEmail, setEventEmail] = useState("");
    const [eventEmailSubmitting, setEventEmailSubmitting] = useState(false);
    const winnerResultsVisible =
        !showWinnerResultsAt ||
        new Date() >= new Date(showWinnerResultsAt);
    const sponsorAwardResultsVisible =
        !sponsorAwardResultsShowAt ||
        new Date() >= new Date(sponsorAwardResultsShowAt);
    const currentVotingType = getEventVotingType(eventDoc);
    const [activeMapMarkerType, setActiveMapMarkerType] = useState("all");
    const votingTypeLabelMap = {
    standard: "People's Choice Voting",
    peoples_choice: "People's Choice Voting",
    ranked_choice: "Ranked Choice Voting",
    points: "Judged Points Voting",
    scorecard: "Scorecard Voting",
    };

    const votingTypeLabel =
    votingTypeLabelMap[currentVotingType] ||
    String(currentVotingType || "standard")
        .replaceAll("_", " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

    // =====================================================
    // 🎗 Sponsors Add-on Gate
    // =====================================================
    // ✅ Purpose:
    // - Sponsors are a purchased LGV add-on
    // - Event Home should not load, count, display, map, promote,
    //   or show setup prompts for sponsors unless this add-on is active
    // =====================================================
    const sponsorsEnabled =
    hasPurchasedAddOn(eventDoc, "sponsors") ||
    hasPurchasedAddOn(eventDoc, "sponsor");
    
    // =====================================================
    // ✅ Event Feature Availability
    // =====================================================
    // Sponsors and Vendors only load when their Power Ups are active.
    // Volunteers remain part of the core event package.
    // =====================================================


  
    const vendorsEnabled =
        hasPurchasedAddOn(eventDoc, "vendors") ||
        hasPurchasedAddOn(eventDoc, "vendor");
    const volunteersEnabled = true;

    const auctionEnabled = hasPurchasedAddOn(
        eventDoc,
        "auction"
    );

    const storeEnabled = hasPurchasedAddOn(
        eventDoc,
        "store"
    );

    const eventTagline = eventDoc?.eventTagline || "";
    const shareUrl = `https://letsgovote.app/${eventslug}/event`;
    const shareText = encodeURIComponent(eventDoc?.name || "Let's Go Vote Event");
    const encodedShareUrl = encodeURIComponent(shareUrl);

    const socialLinks = Array.isArray(eventDoc?.socialLinks)
        ? eventDoc.socialLinks.filter((link) => link?.url)
        : [];

    // Existing demo events created before map seeding was added still receive
    // the bundled venue map. Real events continue to require their own map.
    const eventMapUrl =
        settings?.eventMap ||
        (eventDoc?.demo ? "/demo-assets/default-event-map.svg" : "");

    useEffect(() => {
        logger.log("📦 Event Home Sponsor Add-on Check", {
            eventId,
            sponsorsEnabled,
        });
console.log(
  "💬 Event Home Communication Content:",
  communicationContent
);
        if (!eventId) return;

        if (!sponsorsEnabled) {
            setSponsors([]);

            logger.log("🎗 Sponsors add-on inactive. Sponsor data will not load.", {
                eventId,
            });

            return;
        }

        const fetchSponsors = async () => {
            try {
                // Do not order in Firestore here. Legacy and early demo
                // sponsors may not have a `level` field, and Firestore omits
                // documents that lack an orderBy field.
                const snap = await getDocs(
                    collection(db, "events", eventId, "sponsors")
                );

                const list = snap.docs
                    .map((doc) => ({ id: doc.id, ...doc.data() }))
                    .filter((sponsor) => {
                        const hasModernApprovalFields =
                            sponsor.paymentStatus ||
                            sponsor.approvalStatus ||
                            sponsor.isApproved !== undefined;

                        if (!hasModernApprovalFields) {
                            return true;
                        }

                        return (
                            sponsor.isApproved === true ||
                            sponsor.approvalStatus === "approved" ||
                            sponsor.paymentStatus === "paid" ||
                            sponsor.paymentStatus === "waived" ||
                            sponsor.paymentStatus === "not_required"
                        );
                    })
                    .sort((a, b) => {
                        const orderDifference =
                            Number(a.displayOrder ?? a.levelId ?? 9999) -
                            Number(b.displayOrder ?? b.levelId ?? 9999);

                        return orderDifference ||
                            String(a.name || a.sponsorName || "").localeCompare(
                                String(b.name || b.sponsorName || ""),
                                undefined,
                                { sensitivity: "base" }
                            );
                    });

                setSponsors(list);

                logger.log("✅ Sponsors loaded for Event Home", {
                    eventId,
                    count: list.length,
                });
            } catch (err) {
                logger.error("❌ Error loading sponsors:", { value: err });
                setSponsors([]);
            }
        };

        fetchSponsors();
    }, [eventId, sponsorsEnabled]);

    useEffect(() => {
        // ✅ Purpose:
        // - Load vendors for Event Home
        // - Supports vendor count card, featured vendor display, setup progress, and map markers
        // - Vendors are stored separately from sponsors in /events/{eventId}/vendors

        if (!eventId) return;

        if (!vendorsEnabled) {
            setVendors([]);
            return;
        }

        const fetchVendors = async () => {
            try {
                const vendorsSnap = await getDocs(
                    collection(db, "events", eventId, "vendors")
                );

                const list = vendorsSnap.docs
                    .map((docSnap) => ({
                        id: docSnap.id,
                        ...docSnap.data(),
                    }))
                    .sort((a, b) =>
                        String(a.name || "").localeCompare(
                            String(b.name || ""),
                            undefined,
                            {
                                sensitivity: "base",
                            }
                        )
                    );

                setVendors(list);

                logger.log("✅ Vendors loaded for Event Home", {
                    eventId,
                    count: list.length,
                });
            } catch (err) {
                logger.error("❌ Error loading vendors for Event Home:", {
                    value: err,
                });

                setVendors([]);
            }
        };

        fetchVendors();
    }, [eventId, vendorsEnabled]);

    // =====================================================
    // 🛒 Load Store Products + Variant Inventory
    // =====================================================
    // ✅ Purpose:
    // - Loads active Store products for Event Home promotion
    // - Loads active variants for each product so stock is accurate
    // - Calculates live available inventory from variants
    // - Avoids stale parent product inventory totals showing false Sold Out
    // =====================================================

    useEffect(() => {
        if (!eventId) return;

        if (!storeEnabled) {
            setStoreProducts([]);
            return;
        }

        const loadStoreProducts = async () => {
            try {
                const snap = await getDocs(
                    collection(db, "events", eventId, "store_items")
                );

                const products = [];

                for (const productDoc of snap.docs) {
                    const product = {
                        id: productDoc.id,
                        ...productDoc.data(),
                    };

                    if (product.active === false) {
                        continue;
                    }

                    const variantsSnap = await getDocs(
                        collection(
                            db,
                            "events",
                            eventId,
                            "store_items",
                            productDoc.id,
                            "variants"
                        )
                    );

                    const variants = variantsSnap.docs
                        .map((variantDoc) => ({
                            id: variantDoc.id,
                            ...variantDoc.data(),
                        }))
                        .filter((variant) => variant.active !== false);

                    const liveAvailableInventory = variants.reduce(
                        (sum, variant) =>
                            sum + Number(variant.availableInventory || 0),
                        0
                    );

                    const liveReceivedInventory = variants.reduce(
                        (sum, variant) =>
                            sum + Number(variant.receivedInventory || 0),
                        0
                    );

                    products.push({
                        ...product,
                        variants,
                        liveAvailableInventory,
                        liveReceivedInventory,
                    });
                }

                products.sort((a, b) => {
                    const aFeatured = a.featured ? 1 : 0;
                    const bFeatured = b.featured ? 1 : 0;

                    if (bFeatured !== aFeatured) {
                        return bFeatured - aFeatured;
                    }

                    return Number(a.displayOrder || 100) - Number(b.displayOrder || 100);
                });

                setStoreProducts(products);

                logger.log("🛒 Store products loaded with live inventory", {
                    count: products.length,
                });
            } catch (err) {
                logger.error("❌ Error loading Store products:", {
                    value: err,
                });

                setStoreProducts([]);
            }
        };

        loadStoreProducts();
    }, [eventId, storeEnabled]);

    useEffect(() => {
        // ✅ Purpose:
        // - Load public event schedule items for Event Home
        // - Schedule items are public read data
        // - Items are sorted by schedule date, then start time
        // - Public display visibility is controlled separately by scheduleShowDaysBefore

        const fetchScheduleItems = async () => {
            if (!eventId) return;

            try {
                logger.log("📅 Loading Event Home schedule items", {
                    eventId,
                });

                const scheduleQuery = query(
                    collection(db, "events", eventId, "schedule_items")
                );

                const snap = await getDocs(scheduleQuery);

                const list = snap.docs
                    .map((docSnap) => ({
                        id: docSnap.id,
                        ...docSnap.data(),
                    }))
                    .sort((a, b) => {
                        const dateCompare = String(
                            a.scheduleDate || ""
                        ).localeCompare(
                            String(b.scheduleDate || "")
                        );

                        if (dateCompare !== 0) {
                            return dateCompare;
                        }

                        return (
                            Number(a.sortStartMinutes || 0) -
                            Number(b.sortStartMinutes || 0)
                        );
                    });

                setScheduleItems(list);

                logger.log("✅ Event Home schedule items loaded", {
                    eventId,
                    count: list.length,
                });
            } catch (err) {
                logger.error("❌ Error loading Event Home schedule items:", {
                    value: err,
                });

                setScheduleItems([]);
            }
        };

        fetchScheduleItems();
    }, [eventId]);

    useEffect(() => {
        // ✅ Purpose:
        // - Determine whether the logged-in user can manage this event
        // - This allows manager/admin-only setup placeholders to show
        // - Public voters will never see these setup prompts

        const checkManageAccess = async () => {
            if (!eventId || !user?.uid) {
                setCanManageEvent(false);
                return;
            }

            try {
                // ✅ Check global user record first for platform admin access
                const globalUserRef = doc(db, "users", user.uid);
                const globalUserSnap = await getDoc(globalUserRef);
                const globalUserData = globalUserSnap.exists() ? globalUserSnap.data() : {};

                // ✅ Check event-scoped user record for manager/admin access
                const eventUserRef = doc(db, "events", eventId, "users", user.uid);
                const eventUserSnap = await getDoc(eventUserRef);
                const eventUserData = eventUserSnap.exists() ? eventUserSnap.data() : {};
                const explicitlyPendingManager =
                    eventUserData?.pendingManager === true;

                const hasAccess =
                    globalUserData?.role === "admin" ||
                    (
                        !explicitlyPendingManager &&
                        (
                            eventDoc?.ownerId === user.uid ||
                            eventDoc?.managerIds?.includes?.(user.uid) ||
                            eventUserData?.role === "admin" ||
                            eventUserData?.role === "manager"
                        )
                    );

                setCanManageEvent(hasAccess);

                logger.log("🔐 Event Home manage access checked", {
                    eventId,
                    uid: user.uid,
                    hasAccess,
                });
            } catch (err) {
                logger.error("❌ Error checking event home manage access:", {
                    value: err,
                });

                setCanManageEvent(false);
            }
        };

        checkManageAccess();
    }, [eventDoc, eventId, user]);

    useEffect(() => {
        // ✅ Purpose:
        // - Load volunteer activity count for this event
        // - Public users only see volunteer CTA when activities exist
        // - Managers/admins see a setup placeholder when none exist

        const fetchVolunteerActivityCount = async () => {
            if (!eventId) return;

            try {
                const snap = await getDocs(collection(db, "events", eventId, "volunteers"));
                setVolunteerActivityCount(snap.size);

                logger.log("🙋 Volunteer activities loaded for Event Home", {
                    eventId,
                    count: snap.size,
                });
            } catch (err) {
                logger.error("❌ Error loading volunteer activity count:", {
                    value: err,
                });

                setVolunteerActivityCount(0);
            }
        };

        fetchVolunteerActivityCount();
    }, [eventId]);

    useEffect(() => {
        // ✅ Purpose:
        // - Load public-facing volunteer content from volunteer settings
        // - Supports Contact the Event Promoter and Volunteer Goodies messaging
        // - These values are shown on Event Home for public users
        // - Managers/admins see missing-content CTAs when these are empty

        const fetchVolunteerSettings = async () => {
            if (!eventId) return;

            try {
                const snap = await getDoc(
                    doc(db, "events", eventId, "settings", "volunteer_settings")
                );

                const data = snap.exists() ? snap.data() : {};

                setVolunteerContactPromoter(data.contactPromoter || "");
                setVolunteerGoodies(data.volunteerGoodies || "");

                logger.log("🙋 Volunteer settings loaded for Event Home", {
                    eventId,
                    hasContactPromoter: Boolean(data.contactPromoter),
                    hasVolunteerGoodies: Boolean(data.volunteerGoodies),
                });
            } catch (err) {
                logger.error("❌ Error loading volunteer settings for Event Home:", {
                    value: err,
                });

                setVolunteerContactPromoter("");
                setVolunteerGoodies("");
            }
        };

        fetchVolunteerSettings();
    }, [eventId]);

    useEffect(() => {
        // ✅ Purpose:
        // - Load setup counts used by the manager/admin readiness card
        // - Helps managers quickly see what still needs setup
        // - Keeps public users unaffected because the card only shows for managers/admins

        const fetchSetupCounts = async () => {
            if (!eventId || !canManageEvent) return;

            try {
                const requests = [
                    getDocs(collection(db, "events", eventId, "categories")),
                    getDocs(collection(db, "events", eventId, "cars")),
                ];

                if (auctionEnabled) {
                    requests.push(
                        getDocs(collection(db, "events", eventId, "auctionItems"))
                    );
                }

                const [categoriesSnap, entriesSnap, auctionItemsSnap] =
                    await Promise.all(requests);

                setCategoryCount(categoriesSnap.size);

                setCategories(
                    categoriesSnap.docs.map((docSnap) => ({
                        id: docSnap.id,
                        ...docSnap.data(),
                    }))
                );

                setEntryCount(entriesSnap.size);
                setEntries(
                    entriesSnap.docs.map((docSnap) => ({
                        id: docSnap.id,
                        ...docSnap.data(),
                    }))
                );                
                setAuctionItemCount(auctionItemsSnap?.size || 0);

                logger.log("📊 Event Home setup counts loaded", {
                    eventId,
                    categories: categoriesSnap.size,
                    entries: entriesSnap.size,
                    // Auction data is only requested for Auction-enabled events.
                    auctionItems: auctionItemsSnap?.size || 0,
                });
            } catch (err) {
                logger.error("❌ Error loading Event Home setup counts:", {
                    value: err,
                });
            }
        };

        fetchSetupCounts();
    }, [auctionEnabled, eventId, canManageEvent]);

    useEffect(() => {
        // ✅ Purpose:
        // - Load public-safe event counts for Event Home cards
        // - Allows any visitor to see how many entries/categories exist
        // - Uses Firestore count aggregation so we do not download every entry just to show a number
        // - Keeps this separate from manager/admin setup data so restricted reads do not break public counts

        const fetchPublicEventCounts = async () => {
            if (!eventId) return;

            try {
                const entriesCountSnap = await getCountFromServer(
                    collection(db, "events", eventId, "cars")
                );

                const categoriesCountSnap = await getCountFromServer(
                    collection(db, "events", eventId, "categories")
                );

                setEntryCount(entriesCountSnap.data().count || 0);
                setCategoryCount(categoriesCountSnap.data().count || 0);

                logger.log("📊 Public Event Home counts loaded", {
                    eventId,
                    entries: entriesCountSnap.data().count || 0,
                    categories: categoriesCountSnap.data().count || 0,
                });
            } catch (err) {
                logger.error("❌ Error loading public Event Home counts:", {
                    value: err,
                });

                setEntryCount(0);
                setCategoryCount(0);
            }
        };

        fetchPublicEventCounts();
    }, [eventId]);

    useEffect(() => {
        // ✅ Purpose:
        // - Load public Event Home data for all visitors, including logged-out users
        // - Supports public winner results, sponsor awards, entry display, category display, and public event stats
        // - Does not expose manager-only UI; that is still controlled by canManageEvent

        const fetchPublicEventHomeData = async () => {
            if (!eventId) return;

            try {
                const [
                    categoriesSnap,
                    entriesSnap,
                ] = await Promise.all([
                    getDocs(collection(db, "events", eventId, "categories")),
                    getDocs(collection(db, "events", eventId, "cars")),
                ]);

                const categoryList = categoriesSnap.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                }));

                const entryList = entriesSnap.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                }));

                setCategories(categoryList);
                setEntries(entryList);

                setCategoryCount(categoryList.length);
                setEntryCount(entryList.length);

                logger.log("🌎 Public Event Home data loaded", {
                    eventId,
                    categories: categoryList.length,
                    entries: entryList.length,
                });
            } catch (err) {
                logger.error("❌ Error loading public Event Home data:", {
                    value: err,
                });

                setCategories([]);
                setEntries([]);
            }
        };

        fetchPublicEventHomeData();
    }, [eventId]);

    useEffect(() => {
        // Published/result-audience views consume only the server-generated
        // aggregate projection. Raw ballot rows never reach Event Home.
        const fetchEventHomeResults = async () => {
            if (!eventId || !user || categories.length === 0) {
                setResultProjection(null);
                return;
            }

            try {
                const entryMap = Object.fromEntries(
                    entries.map((entry) => [entry.id, entry])
                );
                const resultView = await loadEventResultProjection({
                    eventId,
                    categories,
                    entryMap,
                    leaderboardLimit: winnerPlaceLimit,
                });
                setResultProjection(resultView.projection);
            } catch (err) {
                logger.warn("Event Home results are unavailable to this viewer.", {
                    code: err?.code || "unknown",
                });
                setResultProjection(null);
            }
        };

        fetchEventHomeResults();
    }, [categories, entries, eventId, user, winnerPlaceLimit]);

    const getCommunicationText = (
    section,
    element,
    fallback = ""
    ) => {
    return (
        communicationContent?.content?.[section]?.[element] ||
        communicationContent?.[section]?.[element] ||
        fallback
    );
    };

    // =====================================================
    // 🔎 Event SEO
    // =====================================================

    const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: eventDoc?.name || undefined,
    description:
        settings?.description ||
        eventDoc?.description ||
        undefined,
    startDate: eventDoc?.startDate || undefined,
    endDate: eventDoc?.endDate || undefined,
    image:
        eventDoc?.eventLogo ||
        settings?.eventLogo ||
        undefined,    
    };

    const breadcrumbs = [
    { name: "Home", path: "/" },
    {
        name: eventDoc?.name || "Event",
        path: `/${eventslug}/event`,
    },
    ];

    const approvedEventSeo = buildEventPageSeo({
        eventDoc,
        pageKey: "event_home",
        fallbackTitle: `${eventDoc?.name || "Event"} | Let's Go Vote`,
        fallbackDescription:
            eventDoc?.description ||
            `Vote, view entries, sponsors, volunteers, and event information for ${
            eventDoc?.name || "this event"
            }.`,
        });

        const eventSeo = {
        ...approvedEventSeo,

        path: `/${eventslug}/event`,
        noindex: !isEventPublic(eventDoc),
        structuredData: eventSchema,
        breadcrumbs,
    };

    const sponsorsByLevel = [
        ...sponsorLevels
            .map((level) => ({
                levelName: level.name,
                sponsors: sponsors.filter((sponsor) => sponsor.level === level.name),
            }))
            .filter((group) => group.sponsors.length > 0),

        {
            levelName: "Event Sponsors",
            sponsors: sponsors.filter(
                (sponsor) =>
                    !sponsor.level ||
                    !sponsorLevels.some((level) => level.name === sponsor.level)
            ),
        },
    ].filter((group) => group.sponsors.length > 0);

    const sponsorsWithMapMarkers = sponsors
    .filter(
        (sponsor) =>
        sponsor.mapMarkerLabel ||
        sponsor.markerLabel ||
        sponsor.label ||
        sponsor.boothNumber ||
        sponsor.mapLabel ||
        sponsor.locationLabel ||
        sponsor.mapMarkerTitle ||
        sponsor.mapMarkerNote ||
        sponsor.mapMarkerX !== null ||
        sponsor.mapMarkerY !== null
    )
    .map((sponsor) => ({
        ...sponsor,
        mapMarkerType: "sponsor",

        // ✅ Normalize sponsor marker label so Event Home does not fall back to "S"
        mapMarkerLabel:
        sponsor.mapMarkerLabel ||
        sponsor.markerLabel ||
        sponsor.label ||
        sponsor.boothNumber ||
        sponsor.mapLabel ||
        sponsor.locationLabel ||
        "",

        // ✅ Normalize sponsor marker title
        mapMarkerTitle:
        sponsor.mapMarkerTitle ||
        sponsor.title ||
        "",

        // ✅ Normalize sponsor marker note
        mapMarkerNote:
        sponsor.mapMarkerNote ||
        sponsor.note ||
        "",
    }))
    .sort((a, b) =>
        String(a.name || "").localeCompare(
        String(b.name || ""),
        undefined,
        {
            sensitivity: "base",
        }
        )
    );

    const eventLocationMarkers = Array.isArray(settings?.eventLocationMarkers)
        ? settings.eventLocationMarkers.map((marker) => ({
            ...marker,
            mapMarkerType: "event_location",

            mapMarkerLabel:
                marker.mapMarkerLabel ||
                marker.label ||
                "",

            mapMarkerTitle:
                marker.mapMarkerTitle ||
                marker.title ||
                "",

            mapMarkerNote:
                marker.mapMarkerNote ||
                marker.note ||
                "",
        }))
        : [];

    const vendorsWithMapMarkers = vendors
        .filter(
            (vendor) =>
                vendor.mapMarkerLabel ||
                vendor.mapMarkerTitle ||
                vendor.mapMarkerNote ||
                vendor.mapMarkerX !== null ||
                vendor.mapMarkerY !== null
        )
        .map((vendor) => ({
            ...vendor,
            mapMarkerType: "vendor",
        }))
        .sort((a, b) =>
            String(a.name || "").localeCompare(
                String(b.name || ""),
                undefined,
                {
                    sensitivity: "base",
                }
            )
        );

    const allMapMarkers = [
        ...sponsorsWithMapMarkers,
        ...vendorsWithMapMarkers,
        ...eventLocationMarkers,
    ];

    const mapMarkerTypeConfig = {
    all: {
        label: "All Locations",
        buttonLabel: "🗺️ Show All",
        markerLabel: "",
        className: "bg-dark text-white",
    },
    sponsor: {
        label: "Sponsors",
        buttonLabel: "🌟 Sponsors",
        markerLabel: "S",
        className: "bg-success text-white",
    },
    vendor: {
        label: "Vendors",
        buttonLabel: "🛍️ Vendors",
        markerLabel: "V",
        className: "bg-primary text-white",
    },
    event_location: {
        label: "Event Locations",
        buttonLabel: "📍 Event Locations",
        markerLabel: "E",
        className: "bg-warning text-dark",
    },
    };

    const activeMapConfig =
    mapMarkerTypeConfig[activeMapMarkerType] || mapMarkerTypeConfig.sponsor;

    const filteredMapMarkers =
        activeMapMarkerType === "all"
            ? allMapMarkers
            : allMapMarkers.filter(
                (marker) => (marker.mapMarkerType || "sponsor") === activeMapMarkerType
            );
            
    const filteredPlacedMapMarkers = filteredMapMarkers.filter(
    (marker) =>
        marker.mapMarkerX !== null &&
        marker.mapMarkerX !== undefined &&
        marker.mapMarkerY !== null &&
        marker.mapMarkerY !== undefined
    );

    const getSponsorAwardWinnerEntry = (sponsor) => {
        const winnerReg = String(sponsor.sponsorAwardWinnerReg || "").trim();

        if (!winnerReg) return null;

        return entries.find(
            (entry) => String(entry.regcnt || "").trim() === winnerReg
        ) || null;
    };

    // - Calculates Event Home winner results based on the current event voting engine
    // - Supports People's Choice, Ranked Choice, Judged Points, and Scorecard-style results
    // - Keeps Event Home display consistent while allowing each voting type to rank entries correctly
    const getEntryOwnerName = (entry) =>
        entry?.ownerName ||
        `${entry?.ownerFirst || ""} ${entry?.ownerLast || ""}`.trim() ||
        entry?.name ||
        "Unknown Owner";

    const getEntryLabel = (entry) =>
        entry?.description ||
        `${entry?.year || ""} ${entry?.model || ""}`.trim() ||
        "Entry details unavailable";

    const calculatePeopleChoiceWinners = (category) => {
        return (resultProjection?.standard?.entries || [])
            .filter((row) => String(row.catid) === String(category.id))
            .map((row) => ({
                entry: entries.find((entry) => entry.id === row.cid),
                scoreLabel: `${row.count} vote${row.count === 1 ? "" : "s"}`,
                sortValue: row.count,
            }))
            .filter((item) => item.entry)
            .sort((a, b) => b.sortValue - a.sortValue)
            .slice(0, winnerPlaceLimit);
    };

    const calculateRankedChoiceWinners = (category) => {
        const categoryResult = (resultProjection?.ranked?.categories || [])
            .find((row) => String(row.catid) === String(category.id));

        return (categoryResult?.leaderboardPlacements || [])
            .map((row) => ({
                entry: entries.find((entry) => entry.id === row.cid),
                scoreLabel: `Ranked-choice place ${row.place}`,
                sortValue: -Number(row.place),
            }))
            .filter((item) => item.entry)
            .sort((a, b) => b.sortValue - a.sortValue)
            .slice(0, winnerPlaceLimit);
    };

    const calculateScoredWinners = (category) => {
        return (resultProjection?.points?.entries || [])
            .filter((row) => String(row.catid) === String(category.id))
            .map((row) => ({
                entry: entries.find((entry) => entry.id === row.cid),
                scoreLabel: `${Number(row.averageScore || 0).toFixed(2)} avg score`,
                sortValue: Number(row.averageScore || 0),
            }))
            .filter((item) => item.entry)
            .sort((a, b) => b.sortValue - a.sortValue)
            .slice(0, winnerPlaceLimit);
    };

    const getWinnersForCategory = (category) => {
        if (currentVotingType === "ranked_choice") {
            return calculateRankedChoiceWinners(category);
        }

        if (
            currentVotingType === "points" ||
            currentVotingType === "scorecard"
        ) {
            return calculateScoredWinners(category);
        }

        return calculatePeopleChoiceWinners(category);
    };

    const categoryWinners = categories
        .map((category) => ({
            category,
            winners: getWinnersForCategory(category),
        }))
        .filter((item) => item.winners.length > 0)
        .sort((a, b) => {
            const sortA = Number.isFinite(Number(a?.category?.sortOrder))
                ? Number(a.category.sortOrder)
                : Number.isFinite(Number(a?.category?.id))
                ? Number(a.category.id)
                : 9999;

            const sortB = Number.isFinite(Number(b?.category?.sortOrder))
                ? Number(b.category.sortOrder)
                : Number.isFinite(Number(b?.category?.id))
                ? Number(b.category.id)
                : 9999;

            if (sortA !== sortB) {
                return sortA - sortB;
            }

            return String(a.category.name || a.category.description || "")
                .localeCompare(
                    String(b.category.name || b.category.description || ""),
                    undefined,
                    { sensitivity: "base" }
                );
        });

    const totalVotesCast = resultProjection?.totalBallots || 0;

    const totalVoters = resultProjection?.participantCount || 0;

    const getEventDateValue = (value) => {
        if (!value) return null;

        if (value?.toDate) {
            return value.toDate();
        }

        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const eventEndDate =
        getEventDateValue(eventDoc?.endDate) ||
        getEventDateValue(eventDoc?.startDate);

    const eventDayHasPassed =
        eventEndDate &&
        new Date() > new Date(
            eventEndDate.getFullYear(),
            eventEndDate.getMonth(),
            eventEndDate.getDate(),
            23,
            59,
            59
        );

    const eventStartDate = getEventDateValue(eventDoc?.startDate);

    const eventStartOfDay = eventStartDate
        ? new Date(
            eventStartDate.getFullYear(),
            eventStartDate.getMonth(),
            eventStartDate.getDate(),
            0,
            0,
            0
        )
        : null;

    const scheduleVisible = (() => {
        const showDays = Number(eventDoc?.scheduleShowDaysBefore ?? 7);

        const eventStartDate = getEventDateValue(eventDoc?.startDate);

        if (!eventStartDate) return true;

        const visibleDate = new Date(eventStartDate);

        visibleDate.setDate(visibleDate.getDate() - showDays);

        return new Date() >= visibleDate;
    })();

    const eventHomePhase = eventDayHasPassed
        ? "after_event"
        : eventStartOfDay && new Date() >= eventStartOfDay
        ? "event_day"
        : scheduleVisible
        ? "countdown_window"
        : "before_event";

    const currentSectionOrder =
        EVENT_HOME_SECTION_ORDER[eventHomePhase] ||
        EVENT_HOME_SECTION_ORDER.before_event;

    const getSectionOrderIndex = (sectionId) => {
        const index = currentSectionOrder.indexOf(sectionId);
        return index === -1 ? 999 : index;
    };

    const formatScheduleTime = (timeValue) => {
        if (!timeValue) return "";

        const [hours, minutes] = timeValue.split(":").map(Number);

        const date = new Date();

        date.setHours(hours, minutes, 0, 0);

        return date.toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
        });
    };

    const getScheduleTimeLabel = (item) => {
        const startLabel = formatScheduleTime(item.startTime);

        if (!item.endTime) {
            return `${startLabel} - end`;
        }

        return `${startLabel} - ${formatScheduleTime(item.endTime)}`;
    };

    const formatScheduleDate = (dateValue) => {
        if (!dateValue) return "Schedule Date Not Set";

        return new Date(`${dateValue}T00:00:00`).toLocaleDateString([], {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
        });
    };

    const scheduleItemsByDate = scheduleItems.reduce((groups, item) => {
        const dateKey = item.scheduleDate || "unscheduled";

        if (!groups[dateKey]) {
            groups[dateKey] = [];
        }

        groups[dateKey].push(item);

        return groups;
    }, {});

    const sections = [
        {
            id: "event_details",
            title: "🎟️ Event Details + Pricing + Ticket Purchasing",
            content: description,
            adminMessage: "Public event information is missing.",
            adminHelperText:
                "Attendees use this section to learn event details, pricing, schedules, rules, and ticket information. Adding content improves the attendee experience and reduces questions.",
            adminLink: `/${eventslug}/content`,
            adminButton: "Update Event Details",
            showManagerCtaWhenEmpty: true,
        },
        {
            id: "sponsors",
            title: "🏁 Sponsors",
            content: "__SPONSORS__",
            adminMessage: "Sponsors have not been added yet.",
            adminHelperText:
                "Sponsors are displayed on the public event page so attendees can see who is supporting the event. Add sponsors to improve the event page and promote supporters.",
            adminLink: `/${eventslug}/sponsors_list`,
            adminButton: "Manage Sponsors",
            showManagerCtaWhenEmpty: true,
        },
        {
            id: "volunteers",
            title: "🙋 Volunteer Opportunities",
            content: "__VOLUNTEERS__",
            adminMessage: "Volunteer opportunities have not been configured.",
            adminHelperText:
                "Volunteers help support registration, judging, parking, setup, cleanup, and other event activities. Create volunteer opportunities so attendees can sign up.",
            adminLink: `/${eventslug}/volunteer_opportunities`,
            adminButton: "Create Volunteer Opportunities",
            volunteerSettingsLink: `/${eventslug}/volunteer_settings`,
            volunteerSettingsButton: "Update Volunteer Content",
            showManagerCtaWhenEmpty: true,
        },
        {
            id: "participation_tips",
            title: "📢 Participation and Voting Tips",
            content: participationTips,
            adminMessage: "Participation and voting tips are missing.",
            adminHelperText:
                "Use this section for helpful attendee guidance, voting reminders, QR code tips, ballot review information, and anything participants should know before or during the event.",
            adminLink: `/${eventslug}/content`,
            adminButton: "Update Participation and Voting Tips",
            showManagerCtaWhenEmpty: true,
        },
        {
            id: "optional_1",
            title: "A message from the Event Manger",
            content: optionalDetails1,
            adminLink: `/${eventslug}/content`,
            showManagerCtaWhenEmpty: false,
        },
        {
            id: "optional_2",
            title: "Everything else you want to know",
            content: optionalDetails2,
            adminLink: `/${eventslug}/content`,
            showManagerCtaWhenEmpty: false,
        },
    ];

    const visibleSections = sections.filter((section) => {

        if (section.content === "__SPONSORS__") {
            if (!sponsorsEnabled) return false;

            return sponsors.length > 0 || (canManageEvent && section.showManagerCtaWhenEmpty);
        }

        if (section.content === "__VOLUNTEERS__") {
            if (eventDayHasPassed) return true;

            return volunteerActivityCount > 0 || (canManageEvent && section.showManagerCtaWhenEmpty);
        }

        return (
            String(section.content || "").trim() ||
            (canManageEvent && section.showManagerCtaWhenEmpty)
        );
    }).sort((a, b) => getSectionOrderIndex(a.id) - getSectionOrderIndex(b.id));

    const setupItems = [
        {
            label: "Event Details",
            complete: String(description || "").trim().length > 0,
            link: `/${eventslug}/content`,
            button: "Edit Details",
        },
        {
            label: "Scheduler",
            complete: scheduleItems.length > 0,
            link: `/${eventslug}/scheduler`,
            button: "Manage Schedule",
        },        
        {
            label: "Categories",
            complete: categoryCount > 0,
            link: `/${eventslug}/categories`,
            button: "Manage Categories",
        },
        {
            label: "Entries",
            complete: entryCount > 0,
            link: `/${eventslug}/entries`,
            button: "Manage Entries",
        },
        {
            label: "Sponsors",
            complete: sponsors.length > 0,
            link: `/${eventslug}/sponsors_list`,
            button: "Manage Sponsors",
            hidden: !sponsorsEnabled,
        },
        {
            label: "Vendors",
            complete: vendors.length > 0,
            link: `/${eventslug}/vendors_list`,
            button: "Manage Vendors",
            hidden: !vendorsEnabled,
        },
        {
            label: "Volunteers",
            complete: volunteerActivityCount > 0,
            link: `/${eventslug}/volunteer_opportunities`,
            button: "Manage Volunteers",
            hidden: !volunteersEnabled,
        },
        {
            label: "Auction Items",
            complete: !auctionEnabled || auctionItemCount > 0,
            link: `/${eventslug}/auctions`,
            button: "Manage Auctions",
            hidden: !auctionEnabled,
        },     
        {
            label: "Store",
            complete: storeProducts.length > 0,
            link: `/${eventslug}/store_products`,
            button: "Manage Store",
            hidden: !storeEnabled,
        },        
        {
        label: "Social Links",
        complete: socialLinks.length > 0,
        link: `/${eventslug}/event_settings`,
        button: "Add Social Links",
        },        
    ];

    const visibleSetupItems = setupItems.filter((item) => !item.hidden);
    const completedSetupItems = visibleSetupItems.filter((item) => item.complete).length;
    const setupPercent =
        visibleSetupItems.length > 0
            ? Math.round((completedSetupItems / visibleSetupItems.length) * 100)
            : 0;

    useEffect(() => {
        setSetupProgressExpanded(setupPercent < 100);
    }, [eventId, setupPercent]);

    // ✅ Purpose:
    // - Render a consistent manager/admin setup CTA for missing public content
    // - Makes empty Event Home sections feel intentional and actionable
    // - Helps managers understand what is missing, why it matters, and where to fix it
    // ✅ Restyled setup placeholder for manager/admin missing-content prompts
    const renderSetupPlaceholder = ({
        message,
        helperText,
        link,
        button,
    }) => (
        <div className="alert alert-warning d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-0">
            <div>
                <strong>⚠ {message}</strong>
                <br />
                <span>{helperText}</span>
            </div>

            <Link to={link} className="btn btn-warning btn-sm">
                {button}
            </Link>
        </div>
    );

    // - Shows a small manager/admin edit button above public content sections
    // - Makes Event Home easy to maintain without hunting through admin navigation
    const renderManagerEditButton = ({ link, button }) => {
        if (!canManageEvent || !link || !button) return null;

        return (
            <div className="text-end mb-2">
                <Link to={link} className="btn btn-outline-warning btn-sm">
                    ✏️ {button}
                </Link>
            </div>
        );
    };

    // - Render rich text content from admin/settings
    // - Keeps Quill-rendered content styling consistent
    // ✅ Restyled rich content renderer
    const renderRichContent = (content, section = null) => (
        <>
            {section && renderManagerEditButton({
                link: section.adminLink,
                button: section.adminButton || "Edit Content",
            })}

            <div
                className="ql-editor p-0"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
            />
        </>
    );

    const mapHasPublicContent =
        Boolean(eventMapUrl) ||
        sponsorsWithMapMarkers.length > 0 ||
        vendorsWithMapMarkers.length > 0 ||
        eventLocationMarkers.length > 0;

    const mapShouldShow =
        canManageEvent ||
        (scheduleVisible && mapHasPublicContent);

    const orderedEventHomeSections = [
        {
            id: "winners",
            visible: winnerResultsVisible && categoryWinners.length > 0,
            render: () => renderWinnersSection(),
        },
        {
            id: "map",
            visible: mapShouldShow,
            render: () => renderMapSection(),
        },
        {
            id: "schedule",
            visible: (scheduleVisible && scheduleItems.length > 0) || canManageEvent,
            render: () => renderScheduleSection(),
        },
        {
            id: "auction",
            visible: auctionEnabled,
            render: () => renderAuctionSection(),
        },
        {
            id: "store",
            visible:
                storeEnabled &&
                (storeProducts.length > 0 || canManageEvent),
            render: () => renderStoreSection(),
        },        
        {
            id: "vendors",
            visible:
                vendorsEnabled &&
                (vendors.some((vendor) => vendor.featuredVendor) || canManageEvent),
            render: () => renderVendorsSection(),
        },
        ...visibleSections.map((section) => ({
            id: section.id,
            visible: true,
            render: () => renderContentSection(section),
        })),
    ]
    .filter((section) => section.visible)
    .sort(
        (a, b) =>
            getSectionOrderIndex(a.id) -
            getSectionOrderIndex(b.id)
    );

    const renderMapSection = () => (
        <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                <div>
                    <strong>🗺️ Event Map</strong>
                    <div className="small text-muted">
                        Find sponsor booths, vendors, and important event locations.
                    </div>
                </div>

                {canManageEvent && (
                    <Link
                        to={`/${eventslug}/images`}
                        className="btn btn-outline-warning btn-sm align-self-start align-self-md-center"
                    >
                        ✏️ Manage Event Map
                    </Link>
                )}
            </div>

            <div className="card-body">
                {!scheduleVisible && canManageEvent ? (
                    <div className="alert alert-info mb-0">
                        <strong>Event map is configured but not public yet.</strong>
                        <br />
                        The event map follows the same public visibility timing as the schedule:{" "}
                        {Number(eventDoc?.scheduleShowDaysBefore ?? 7)} day
                        {Number(eventDoc?.scheduleShowDaysBefore ?? 7) === 1 ? "" : "s"} before the event.
                    </div>
                ) : !eventMapUrl && sponsorsWithMapMarkers.length === 0 && canManageEvent ? (
                    <div className="alert alert-warning d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-0">
                        <div>
                            <strong>⚠ Event map has not been configured.</strong>
                            <br />
                            Upload an event map and add sponsor marker labels so attendees can find sponsor locations.
                        </div>

                        <Link to={`/${eventslug}/images`} className="btn btn-warning btn-sm">
                            Manage Event Map
                        </Link>
                    </div>
                ) : (
                    <>
                        {eventMapUrl && (
                            <div className="position-relative text-center mb-4 border rounded overflow-hidden bg-white">
                                <img
                                    src={eventMapUrl}
                                    alt={`${eventDoc?.name || "Event"} map`}
                                    className="img-fluid d-block w-100"
                                    style={{
                                        maxHeight: "750px",
                                        objectFit: "contain",
                                    }}
                                />

                                {filteredPlacedMapMarkers.map((marker, index) => (
                                    <div
                                        key={marker.id || `map-marker-${index}`}
                                        className={`position-absolute translate-middle ${
                                            mapMarkerTypeConfig[marker.mapMarkerType || "sponsor"]?.className ||
                                            activeMapConfig.className
                                        } rounded-circle d-flex align-items-center justify-content-center fw-bold shadow`}
                                        style={{
                                            left: `${marker.mapMarkerX}%`,
                                            top: `${marker.mapMarkerY}%`,
                                            width: "38px",
                                            height: "38px",
                                            border: "3px solid #fff",
                                        }}
                                        title={`${
                                            marker.name ||
                                            marker.mapMarkerTitle ||
                                            marker.title ||
                                            marker.label ||
                                            activeMapConfig.label
                                        }${
                                            marker.mapMarkerNote
                                                ? ` - ${marker.mapMarkerNote}`
                                                : marker.note
                                                ? ` - ${marker.note}`
                                                : ""
                                        }`}
                                    >
                                        {marker.mapMarkerLabel ||
                                            marker.markerLabel ||
                                            marker.label ||
                                            marker.boothNumber ||
                                            marker.mapLabel ||
                                            marker.locationLabel ||
                                            mapMarkerTypeConfig[marker.mapMarkerType || "sponsor"]?.markerLabel ||
                                            activeMapConfig.markerLabel}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
                            {Object.entries(mapMarkerTypeConfig)
                                .filter(([typeKey]) => {
                                    if (typeKey === "sponsor") return sponsorsEnabled;
                                    if (typeKey === "vendor") return vendorsEnabled;
                                    return true;
                                })
                                .map(([typeKey, config]) => (
                                    <button
                                        key={typeKey}
                                        type="button"
                                        className={`btn btn-sm ${
                                            activeMapMarkerType === typeKey
                                                ? "btn-dark"
                                                : "btn-outline-secondary"
                                        }`}
                                        onClick={() => setActiveMapMarkerType(typeKey)}
                                    >
                                        {config.buttonLabel}
                                    </button>
                                ))}
                        </div>

                        {filteredMapMarkers.length === 0 && (
                            <div className="alert alert-light border text-center mb-0">
                                No {activeMapConfig.label.toLowerCase()} map markers have been added yet.
                            </div>
                        )}

                        {filteredMapMarkers.length > 0 && (
                            <>
                                <h5 className="fw-bold mb-3">
                                    {activeMapConfig.label}
                                </h5>

                                {(activeMapMarkerType === "all"
                                    ? Object.entries(mapMarkerTypeConfig)
                                        .filter(([typeKey]) => typeKey !== "all")
                                        .map(([typeKey, config]) => ({
                                            typeKey,
                                            config,
                                            markers: filteredMapMarkers.filter(
                                                (marker) =>
                                                    (marker.mapMarkerType || "sponsor") === typeKey
                                            ),
                                        }))
                                        .filter((group) => group.markers.length > 0)
                                    : [
                                        {
                                            typeKey: activeMapMarkerType,
                                            config: activeMapConfig,
                                            markers: filteredMapMarkers,
                                        },
                                    ]
                                ).map((group) => (
                                    <div key={group.typeKey} className="mb-4">
                                        {activeMapMarkerType === "all" && (
                                            <h6 className="fw-bold border-bottom pb-2 mb-3">
                                                {group.config.buttonLabel}
                                            </h6>
                                        )}

                                        <div className="row g-3">
                                            {group.markers.map((marker, index) => {
                                                const markerKey = marker.id || `${group.typeKey}-${index}`;

                                                const markerLabel =
                                                    marker.mapMarkerLabel ||
                                                    marker.markerLabel ||
                                                    marker.label ||
                                                    marker.boothNumber ||
                                                    marker.mapLabel ||
                                                    marker.locationLabel ||
                                                    "";

                                                const businessTitle =
                                                    marker.name ||
                                                    marker.mapMarkerTitle ||
                                                    marker.title ||
                                                    marker.label ||
                                                    "Map Location";

                                                const markerTitle =
                                                    group.typeKey === "event_location"
                                                        ? ""
                                                        : marker.mapMarkerTitle || marker.title || "";

                                                const markerNote =
                                                    marker.mapMarkerNote ||
                                                    marker.note ||
                                                    "";

                                                const markerLink =
                                                    group.typeKey === "vendor"
                                                        ? `/${eventslug}/vendors/${marker.vendorSlug || marker.id}`
                                                        : group.typeKey === "sponsor"
                                                        ? `/${eventslug}/sponsors/${marker.sponsorSlug || marker.id}`
                                                        : null;

                                                return (
                                                    <div key={markerKey} className="col-12 col-md-6 col-xl-4">
                                                        <div className="border rounded p-3 h-100 bg-light">
                                                            <div className="d-flex align-items-start gap-3">
                                                                {markerLabel && (
                                                                    <div
                                                                        className={`${group.config.className} rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0`}
                                                                        style={{
                                                                            width: "44px",
                                                                            height: "44px",
                                                                            fontSize: "1rem",
                                                                        }}
                                                                    >
                                                                        {markerLabel}
                                                                    </div>
                                                                )}

                                                                <div>
                                                                    {markerLink ? (
                                                                        <Link
                                                                            to={markerLink}
                                                                            className="fw-bold text-dark text-decoration-none"
                                                                        >
                                                                            {businessTitle}
                                                                        </Link>
                                                                    ) : (
                                                                        <div className="fw-bold text-dark">
                                                                            {businessTitle}
                                                                        </div>
                                                                    )}

                                                                    {markerTitle && (
                                                                        <div className="small text-muted mt-1">
                                                                            {markerTitle}
                                                                        </div>
                                                                    )}

                                                                    {markerNote && (
                                                                        <div className="small mt-2">
                                                                            {markerNote}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}

                        {!eventMapUrl && sponsorsWithMapMarkers.length > 0 && (
                            <div className="alert alert-warning mt-3 mb-0">
                                Sponsor map locations have been added, but no event map image has been uploaded yet.
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );

    const renderScheduleSection = () => (
        <>
        <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                <div>
                    <strong>📅 Event Schedule</strong>
                    <div className="small text-muted">
                        Activity times and important event schedule details.
                    </div>
                </div>

                {canManageEvent && (
                    <Link
                        to={`/${eventslug}/scheduler`}
                        className="btn btn-outline-warning btn-sm align-self-start align-self-md-center"
                    >
                        ✏️ Manage Schedule
                    </Link>
                )}
            </div>

            <div className="card-body">
                {!scheduleVisible && canManageEvent ? (
                    <div className="alert alert-info mb-0">
                        <strong>Schedule is configured but not public yet.</strong>
                        <br />
                        The schedule will show publicly {Number(eventDoc?.scheduleShowDaysBefore ?? 7)} day
                        {Number(eventDoc?.scheduleShowDaysBefore ?? 7) === 1 ? "" : "s"} before the event.
                    </div>
                ) : scheduleItems.length === 0 && canManageEvent ? (
                    renderSetupPlaceholder({
                        message: "Event schedule has not been configured.",
                        helperText:
                            "Add schedule items so attendees know when registration, judging, awards, and other activities happen.",
                        link: `/${eventslug}/scheduler`,
                        button: "Manage Schedule",
                    })
                ) : (
                    Object.entries(scheduleItemsByDate).map(([dateKey, items]) => (
                        <div key={dateKey} className="mb-4">
                            <h5 className="fw-bold border-bottom pb-2 mb-3">
                                📅 {formatScheduleDate(dateKey)}
                            </h5>

                            <div className="list-group">
                                {items.map((item) => (
                                    <div
                                        key={item.id}
                                        className="list-group-item"
                                    >
                                        <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                                            <div>
                                                <div className="fw-bold">
                                                    {item.title}
                                                </div>

                                                {item.location && (
                                                    <div className="small text-muted">
                                                        📍 {item.location}
                                                    </div>
                                                )}

                                                {item.description && (
                                                    <div className="small text-muted mt-1">
                                                        {item.description}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="fw-bold text-md-end">
                                                🕘 {getScheduleTimeLabel(item)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
        </>
    );

    const renderVendorsSection = () => {
        const featuredVendors = vendors.filter((vendor) => vendor.featuredVendor);
        const displayedVendors = featuredVendors.length > 0 ? featuredVendors : vendors;

        return (
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                    <div>
                        <strong>🛍 Featured Vendors</strong>
                        <div className="small text-muted">
                            Explore vendors participating in this event.
                        </div>
                    </div>

                    {vendors.length > 0 && (
                        <Link
                            to={`/${eventslug}/vendors`}
                            className="btn btn-outline-primary btn-sm align-self-start align-self-md-center"
                        >
                            View All Vendors
                        </Link>
                    )}
                </div>

                <div className="card-body">
                    {vendors.length === 0 && canManageEvent ? (
                        renderSetupPlaceholder({
                            message: "Vendors have not been added yet.",
                            helperText:
                                "Add vendors so attendees can find food, merchandise, services, booths, and vendor map locations.",
                            link: `/${eventslug}/vendors_list`,
                            button: "Manage Vendors",
                        })
                    ) : (
                        <div className="row g-4">
                            {displayedVendors.slice(0, 6).map((vendor) => (
                                <div key={vendor.id} className="col-12 col-md-6 col-xl-4">
                                    <div className="card h-100 border shadow-sm">
                                        <div className="card-body d-flex flex-column">
                                            <Link
                                                to={`/${eventslug}/vendors/${vendor.vendorSlug || vendor.id}`}
                                                className="text-decoration-none"
                                            >
                                                <div
                                                    className="d-flex align-items-center justify-content-center bg-white border rounded mb-3"
                                                    style={{ minHeight: "130px" }}
                                                >
                                                    {vendor.logoUrl ? (
                                                        <img
                                                            src={vendor.logoUrl}
                                                            alt={vendor.name || "Vendor logo"}
                                                            className="img-fluid"
                                                            style={{
                                                                maxHeight: "110px",
                                                                objectFit: "contain",
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="text-muted small">No Logo</div>
                                                    )}
                                                </div>

                                                <h5 className="fw-bold text-dark text-center mb-2">
                                                    {vendor.name || "Unnamed Vendor"}
                                                </h5>
                                            </Link>

                                            <div className="text-center mb-3">
                                                {vendor.boothNumber && (
                                                    <span className="badge bg-primary me-2">
                                                        📍 {vendor.boothNumber}
                                                    </span>
                                                )}

                                                {vendor.category && (
                                                    <span className="badge bg-secondary">
                                                        {vendor.category}
                                                    </span>
                                                )}
                                            </div>

                                            {vendor.description && (
                                                <div
                                                    className="ql-editor small p-0 text-muted"
                                                    dangerouslySetInnerHTML={{
                                                        __html: sanitizeHtml(vendor.description),
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {vendors.length > 0 && featuredVendors.length === 0 && canManageEvent && (
                        <div className="alert alert-warning mt-4 mb-0">
                            <strong>⚠ No featured vendors selected.</strong>
                            <br />
                            Vendors exist, but none are marked as featured. Showing all vendors for now.
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderStoreSection = () => {
        const featuredProducts = storeProducts.filter(
            (product) => product.featured === true
        );

        const displayedProducts =
            featuredProducts.length > 0
                ? featuredProducts
                : storeProducts;

        return (
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                    <div>
                        <strong>🛒 Store</strong>
                        <div className="small text-muted">
                            Browse event merchandise, shirts, hats, stickers, and more.
                        </div>
                    </div>

                    {storeProducts.length > 0 && (
                        <Link
                            to={`/${eventslug}/store`}
                            className="btn btn-outline-primary btn-sm align-self-start align-self-md-center"
                        >
                            Shop Store
                        </Link>
                    )}
                </div>

                <div className="card-body">
                    {storeProducts.length === 0 && canManageEvent ? (
                        renderSetupPlaceholder({
                            message: "Store is enabled, but no products have been added yet.",
                            helperText:
                                "Add Store products so attendees can buy event merchandise, swag, and leftover inventory.",
                            link: `/${eventslug}/store_products`,
                            button: "Manage Store Products",
                        })
                    ) : (
                        <div className="row g-4">
                            {displayedProducts.slice(0, 6).map((product) => (
                                <div key={product.id} className="col-12 col-md-6 col-xl-4">
                                    <div className="card h-100 border shadow-sm">
                                        <div className="card-body d-flex flex-column">
                                            <Link
                                                to={`/${eventslug}/store/${product.productSlug || product.id}`}
                                                className="text-decoration-none"
                                            >
                                                <div
                                                    className="d-flex align-items-center justify-content-center bg-white border rounded mb-3"
                                                    style={{ minHeight: "150px" }}
                                                >
                                                    {product.imageUrl ? (
                                                        <img
                                                            src={product.imageUrl}
                                                            alt={product.name || "Store product"}
                                                            className="img-fluid"
                                                            style={{
                                                                maxHeight: "130px",
                                                                objectFit: "contain",
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="text-muted small">
                                                            No Image
                                                        </div>
                                                    )}
                                                </div>

                                                <h5 className="fw-bold text-dark text-center mb-2">
                                                    {product.name || "Store Product"}
                                                </h5>
                                            </Link>

                                            <div className="text-center mb-3">
                                                {product.featured && (
                                                    <span className="badge bg-warning text-dark me-2">
                                                        Featured
                                                    </span>
                                                )}

                                                {Number(product.liveAvailableInventory || 0) > 0 ? (
                                                    <span className="badge bg-success">
                                                        {Number(product.liveAvailableInventory || 0)} Available
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-secondary">
                                                        Sold Out
                                                    </span>
                                                )}
                                            </div>

                                            {product.description && (
                                                <p className="small text-muted text-center">
                                                    {String(product.description).slice(0, 120)}
                                                    {String(product.description).length > 120 ? "..." : ""}
                                                </p>
                                            )}

                                            <Link
                                                to={`/${eventslug}/store/${product.productSlug || product.id}`}
                                                className="btn btn-outline-primary btn-sm mt-auto"
                                            >
                                                View Product
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {storeProducts.length > 0 && featuredProducts.length === 0 && canManageEvent && (
                        <div className="alert alert-warning mt-4 mb-0">
                            <strong>⚠ No featured Store products selected.</strong>
                            <br />
                            Products exist, but none are marked as featured. Showing active products for now.
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderAuctionSection = () => (
        <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white">
                <strong>🏷️ Auction</strong>
            </div>

            <div className="card-body">
                <p className="text-muted mb-3">
                    {auctionItemCount > 0
                        ? `${auctionItemCount} auction item${auctionItemCount === 1 ? "" : "s"} available.`
                        : canManageEvent
                        ? "Auction is enabled, but no items have been added yet."
                        : "Auction items are not listed yet."}
                </p>

                <Link
                    to={auctionItemCount > 0 ? `/${eventslug}/auction` : `/${eventslug}/auctions`}
                    className={auctionItemCount > 0 ? "btn btn-outline-primary btn-sm" : "btn btn-warning btn-sm"}
                >
                    {auctionItemCount > 0 ? "Browse Auction" : "Manage Auction"}
                </Link>
            </div>
        </div>
    );

    const getSafeText = (value, fallback = "") => {
        if (value === null || value === undefined) {
            return fallback;
        }

        if (typeof value === "object") {
            return (
                value.name ||
                value.title ||
                value.description ||
                fallback
            );
        }

        return String(value);
    };

    const renderWinnersSection = () => (
        <div id="event-winners" className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                <strong>🏆 Event Winners</strong>
                {canManageEvent && arePromotionResultsPublished(settings) && resultProjection?.resultsAudience === "voters" && (
                    <PromotionSourceButton source="results" sourceScope="collection" ariaLabel="Promote Published Results" className="btn btn-sm btn-outline-primary" />
                )}
            </div>

            <div className="card-body">
                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-6">
                        <div className="border rounded bg-light p-3 h-100">
                            <div className="small text-muted">Total Votes Cast</div>
                            <div className="fs-3 fw-bold">{totalVotesCast}</div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6">
                        <div className="border rounded bg-light p-3 h-100">
                            <div className="small text-muted">Total Voters</div>
                            <div className="fs-3 fw-bold">{totalVoters}</div>
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    {categoryWinners.map((group) => {
                        console.log("🏆 Winner group", group);

                        const categoryName = getSafeText(
                            group.category?.name ||
                            group.category?.description,
                            "Category"
                        );

                        return (
                            <div key={group.category.id} className="col-12 col-lg-6">
                                <div className="card h-100 border shadow-sm">
                                    <div className="card-header bg-light">
                                        <strong>{categoryName}</strong>
                                    </div>

                                    <div className="card-body">
                                        <div className="row g-3">
                                            {group.winners.map((winner, index) => {
                                                const entry = winner.entry;
                                                const ownerName = getEntryOwnerName(entry);

                                                return (
                                                    <div key={entry.id} className="col-12">
                                                        <div className="card border h-100">
                                                            <div className="card-body">
                                                                <div className="d-flex justify-content-between gap-3">
                                                                    <div>
                                                                        <div className="fw-bold mb-1">
                                                                            {index === 0 && "🥇 1st Place"}
                                                                            {index === 1 && "🥈 2nd Place"}
                                                                            {index === 2 && "🥉 3rd Place"}
                                                                            {index === 3 && "🏅 4th Place"}
                                                                            {index === 4 && "🏅 5th Place"}
                                                                        </div>

                                                                        <div>
                                                                            <strong>{getEntryRegistrationLabel(entry)}</strong>
                                                                            {" — "}
                                                                            {getSafeText(getEntryLabel(entry), "Entry details unavailable")}
                                                                        </div>

                                                                        <div className="small text-muted">
                                                                            👤 {ownerName}
                                                                        </div>

                                                                        {showWinnerVoteCounts && (
                                                                            <div className="small text-muted">
                                                                                🧮 {winner.scoreLabel}
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {getEntryImageUrl(entry, eventDoc) && (
                                                                        <img
                                                                            src={getEntryImageUrl(entry, eventDoc)}
                                                                            alt={entry.description}
                                                                            className="rounded border"
                                                                            style={{
                                                                                width: "110px",
                                                                                height: "90px",
                                                                                objectFit: "cover",
                                                                            }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const renderContentSection = (section) => {
        if (section.content === "__SPONSORS__") {
            return (
                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                        <div>
                            <strong>🏁 Sponsors</strong>
                            <div className="small text-muted">
                            Thank you to the supporters helping make this event happen.
                            </div>
                        </div>

                        {sponsors.length > 0 && (
                            <Link
                            to={`/${eventslug}/sponsors`}
                            className="btn btn-outline-primary btn-sm align-self-start align-self-md-center"
                            >
                            View All Sponsors
                            </Link>
                        )}
                    </div>

                    <div className="card-body">
                        {sponsorsByLevel.length === 0 && canManageEvent &&
                            renderSetupPlaceholder({
                            message: section.adminMessage,
                            helperText: section.adminHelperText,
                            link: section.adminLink,
                            button: section.adminButton,
                            })}

                        {sponsorsByLevel.map((group) => (
                            <div key={group.levelName} className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h4 className="h5 fw-bold mb-0">
                                    🎗 {group.levelName}
                                    </h4>

                                    <span className="badge bg-dark">
                                    {group.sponsors.length} Sponsor{group.sponsors.length === 1 ? "" : "s"}
                                    </span>
                                </div>

                                <div className="row g-4">
                                    {group.sponsors.map((sponsor) => {
                                    const winnerEntry = getSponsorAwardWinnerEntry(sponsor);

                                    return (
                                            <div key={sponsor.id} className="col-12 col-md-6 col-xl-4">
                                                <div className="card h-100 border shadow-sm">
                                                    <div className="card-body d-flex flex-column">
                                                    <Link
                                                        to={`/${eventslug}/sponsors/${sponsor.sponsorSlug || sponsor.id}`}
                                                        className="text-decoration-none"
                                                    >
                                                        <div
                                                        className="d-flex align-items-center justify-content-center bg-white border rounded mb-3"
                                                        style={{ minHeight: "130px" }}
                                                        >
                                                        {sponsor.logoUrl ? (
                                                            <img
                                                            src={sponsor.logoUrl}
                                                            alt={sponsor.name}
                                                            className="img-fluid"
                                                            style={{
                                                                maxHeight: "110px",
                                                                objectFit: "contain",
                                                            }}
                                                            />
                                                        ) : (
                                                            <div className="text-muted small">No Logo</div>
                                                        )}
                                                        </div>

                                                        <h5 className="fw-bold text-dark text-center mb-3">
                                                        {sponsor.name || "Unnamed Sponsor"}
                                                        </h5>
                                                    </Link>

                                                    {(sponsor.sponsorAwardTitle || sponsor.sponsorAwardWinnerReg) && (
                                                        <div className="alert alert-light border mt-auto mb-0">
                                                        <div className="fw-bold text-dark">
                                                            🏆 {sponsor.sponsorAwardTitle || "Sponsor Award"}
                                                        </div>

                                                        {sponsorAwardResultsVisible && winnerEntry ? (
                                                            <div className="mt-2">
                                                            {getEntryImageUrl(winnerEntry, eventDoc) && (
                                                                <div className="text-center mb-3">
                                                                <img
                                                                    src={getEntryImageUrl(winnerEntry, eventDoc)}
                                                                    alt={getEntryRegistrationLabel(winnerEntry)}
                                                                    className="img-fluid rounded border shadow-sm"
                                                                    style={{
                                                                    maxHeight: "180px",
                                                                    objectFit: "contain",
                                                                    backgroundColor: "#fff",
                                                                    }}
                                                                />
                                                                </div>
                                                            )}

                                                            <div>
                                                                <strong>Winner: {getEntryRegistrationLabel(winnerEntry)}</strong>
                                                                {" — "}
                                                                {getEntryLabel(winnerEntry)}
                                                            </div>

                                                            <div className="small text-muted mt-1">
                                                                👤 Owner: {getEntryOwnerName(winnerEntry)}
                                                            </div>
                                                            </div>
                                                        ) : sponsor.sponsorAwardWinnerReg && !sponsorAwardResultsVisible ? (
                                                            <div className="small text-muted mt-2">
                                                            Award winner results will be announced later.
                                                            </div>
                                                        ) : sponsor.sponsorAwardWinnerReg ? (
                                                            <div className="small text-muted mt-2">
                                                            Winner registration #{sponsor.sponsorAwardWinnerReg} has been entered, but the entry could not be matched.
                                                            </div>
                                                        ) : (
                                                            <div className="small text-muted mt-2">
                                                            Winner not selected yet.
                                                            </div>
                                                        )}
                                                        </div>
                                                    )}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (section.content === "__VOLUNTEERS__") {
            return (
                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                    <div>
                        <strong>🙋 Volunteer Opportunities</strong>
                        <div className="small text-muted">
                        Help support this event before, during, or after show day.
                        </div>
                    </div>

                    {!eventDayHasPassed && volunteerActivityCount > 0 && (
                        <Link
                        to={`/${eventslug}/volunteer_opportunities`}
                        className="btn btn-outline-success btn-sm align-self-start align-self-md-center"
                        >
                        View Volunteer Shifts
                        </Link>
                    )}
                    </div>

                    <div className="card-body">
                    {canManageEvent && (
                        <div className="text-end mb-3">
                        <Link
                            to={section.volunteerSettingsLink}
                            className="btn btn-outline-warning btn-sm"
                        >
                            ✏️ {section.volunteerSettingsButton}
                        </Link>
                        </div>
                    )}

                    {eventDayHasPassed ? (
                        <div className="alert alert-info">
                        <div className="fw-bold mb-2">🙋 This event is over.</div>

                        <div className="mb-3">
                            We are happy to take your email for future volunteer opportunities.
                        </div>

                        <form
                            onSubmit={async (e) => {
                            e.preventDefault();

                            const email = futureVolunteerEmail.trim();

                            if (!email) {
                                showStatus(setStatus, {
                                type: "warning",
                                message: "Please enter your email address.",
                                });

                                return;
                            }

                            setFutureVolunteerSubmitting(true);

                            try {
                                await addDoc(
                                collection(db, "events", eventId, "eventInterest"),
                                {
                                    email,
                                    eventId,
                                    eventName: eventDoc?.name || "",
                                    interestType: "volunteer",
                                    source: "event_home_volunteer_closed",
                                    createdAt: serverTimestamp(),
                                }
                                );

                                showStatus(setStatus, {
                                type: "success",
                                message: "Thank you! We saved your email for future volunteer opportunities.",
                                });

                                // 📊 Google Analytics
                                trackEvent({
                                action: GA_EVENTS.VOLUNTEER_INTEREST_SUBMITTED,
                                parameters: {
                                    event_slug: eventslug,
                                    event_id: eventId,
                                    source: "event_home_volunteer_closed",
                                    source_page: window.location.pathname,
                                },
                                });

                                setFutureVolunteerEmail("");
                            } catch (err) {
                                logger.error("❌ Error saving future volunteer interest:", {
                                value: err,
                                });

                                showStatus(setStatus, {
                                type: "error",
                                message: "Unable to save your email right now. Please try again.",
                                });
                            } finally {
                                setFutureVolunteerSubmitting(false);
                            }
                            }}
                        >
                            <div className="row g-2">
                            <div className="col-12 col-md">
                                <input
                                type="email"
                                className="form-control"
                                value={futureVolunteerEmail}
                                onChange={(e) => setFutureVolunteerEmail(e.target.value)}
                                placeholder={getCommunicationText(
                                "stay_connected",
                                "email_placeholder",
                                "Enter your email"
                                )}
                                required
                                />
                            </div>

                            <div className="col-12 col-md-auto">
                                <button
                                type="submit"
                                className="btn btn-primary w-100"
                                disabled={futureVolunteerSubmitting}
                                >
                                {futureVolunteerSubmitting ? "Saving..." : "Keep Me Posted"}
                                </button>
                            </div>
                            </div>
                        </form>
                        </div>
                    ) : volunteerActivityCount === 0 && canManageEvent ? (
                        renderSetupPlaceholder({
                        message: section.adminMessage,
                        helperText: section.adminHelperText,
                        link: section.adminLink,
                        button: section.adminButton,
                        })
                    ) : null}

                    <div className="row g-4">
                        {volunteerContactPromoter && (
                        <div className="col-12 col-md-6">
                            <div className="card h-100 border shadow-sm">
                            <div className="card-header bg-light">
                                <strong>📬 Contact the Event Promoter</strong>
                            </div>

                            <div className="card-body">
                                {renderRichContent(volunteerContactPromoter)}
                            </div>
                            </div>
                        </div>
                        )}

                        {volunteerGoodies && (
                        <div className="col-12 col-md-6">
                            <div className="card h-100 border shadow-sm">
                            <div className="card-header bg-light">
                                <strong>🎁 Volunteer Goodies</strong>
                            </div>

                            <div className="card-body">
                                {renderRichContent(volunteerGoodies)}
                            </div>
                            </div>
                        </div>
                        )}
                    </div>
                    </div>
                </div>
            );
        }

    return (
        <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                <strong>{section.title}</strong>

                {canManageEvent && section.adminLink && (
                    <Link
                        to={section.adminLink}
                        className="btn btn-outline-warning btn-sm align-self-start align-self-md-center"
                    >
                        ✏️ {section.adminButton || "Edit Content"}
                    </Link>
                )}
            </div>

            <div className="card-body">
                {String(section.content || "").trim()
                    ? renderRichContent(section.content)
                    : canManageEvent
                    ? renderSetupPlaceholder({
                        message: section.adminMessage,
                        helperText: section.adminHelperText,
                        link: section.adminLink,
                        button: section.adminButton,
                    })
                    : null}
            </div>
        </div>
    );

};

  // ⛔ Still loading event or settings
  if (!ready || !eventId || eventLoading || loading || communicationLoading) {
    return <FullPageSpinner message="Loading Event Details..." />;
  }

    return (
    <>
        <SeoHead {...eventSeo} />

        <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "1400px" }}>
            {status?.message && (
            <div
                className={`alert ${
                status.type === "success"
                    ? "alert-success"
                    : status.type === "warning"
                    ? "alert-warning"
                    : status.type === "error"
                    ? "alert-danger"
                    : "alert-info"
                }`}
            >
                {status.message}
            </div>
            )}

            <div className="card shadow-sm border-0 mb-4">
            <div className="card-body p-4 p-lg-5">
                <div className="row g-4 align-items-center">
                <div className="col-12 col-lg-8">
                    <div className="mb-3">

                        <span className={`badge me-2 ${isVotingOpen ? "bg-success" : "bg-secondary"}`}>
                            {isVotingOpen ? "🗳️ Voting Open" : "Voting Not Open"}
                        </span>

                        <span className="badge bg-primary me-2">
                        🧮 {votingTypeLabel}
                        </span>

                        <span className="badge bg-light text-dark me-2">
                            🚗 {entryCount} Entr{entryCount === 1 ? "y" : "ies"}
                        </span>

                        <span className="badge bg-light text-dark me-2">
                            🏆 {categoryCount} Categor{categoryCount === 1 ? "y" : "ies"}
                        </span>

                        {sponsorsEnabled && (
                            <span className="badge bg-light text-dark me-2">
                                🌟 {sponsors.length} Sponsor{sponsors.length === 1 ? "" : "s"}
                            </span>
                        )}

                        <span className="badge bg-light text-dark me-2">
                            🛍 {vendors.length} Vendor{vendors.length === 1 ? "" : "s"}
                        </span>     

                        {storeEnabled && (
                            <span className="badge bg-light text-dark me-2">
                                🛒 {storeProducts.length} Product
                                {storeProducts.length === 1 ? "" : "s"}
                            </span>
                        )}                                    
                    </div>

                    <h1 className="display-5 fw-bold mb-2" style={{ color: "#212529" }}>
                    {eventDoc?.name || "Event Home"}
                    </h1>
                    {canManageEvent && (
                        <div className="mb-3">
                            <PromotionSourceButton source="event" sourceScope="collection" ariaLabel="Promote Event Home" />
                        </div>
                    )}

                    {eventTagline && (
                    <p className="lead text-muted mb-2">
                        {eventTagline}
                    </p>
                    )}

                    {eventDoc?.eventLocation && (
                    <p className="text-muted mb-2">
                        📍 {eventDoc.eventLocation}
                    </p>
                    )}

                    {eventDoc?.startDate && (
                        <p className="lead text-muted mb-0">
                        📅{" "}
                        {eventDoc.startDate?.toDate
                        ? eventDoc.startDate.toDate().toLocaleDateString()
                        : new Date(eventDoc.startDate).toLocaleDateString()}
                    </p>
                    )}

                    {socialLinks.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-3">
                        {socialLinks.map((link, index) => (
                        <a
                            key={`${link.platform || "social"}-${index}`}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-dark"
                        >
                            {link.label || "Social Link"}
                        </a>
                        ))}
                    </div>
                    )}
                </div>

                <div className="col-12 col-lg-4">
                    <div className="card bg-light border-0 h-100">
                    <div className="card-body">
                        <h5 className="fw-bold mb-2">
                        📬 {getCommunicationText(
                            "stay_connected",
                            "heading",
                            "Stay Connected"
                        )}
                        </h5>

                        <p className="text-muted small mb-3">
                            {getCommunicationText(
                                "stay_connected",
                                "description",
                                "Keeeep connected by providing your email. We will keep you up to date with future event information."
                            )}
                        </p>

                        <form
                        onSubmit={async (e) => {
                            e.preventDefault();

                            const email = eventEmail.trim();

                            if (!email) {
                            showStatus(setStatus, {
                                type: "warning",
                                message: "Please enter an email address.",
                            });
                            return;
                            }

                            setEventEmailSubmitting(true);

                            try {
                            await addDoc(
                                collection(db, "events", eventId, "eventInterest"),
                                {
                                email,
                                eventId,
                                eventName: eventDoc?.name || "",
                                source: "event_home",
                                createdAt: serverTimestamp(),
                                }
                            );

                            showStatus(setStatus, {
                                type: "success",
                                message: "Thank you! We'll keep you updated about future events.",
                            });

                            setEventEmail("");
                            } catch (err) {
                            logger.error("❌ Error saving event interest:", {
                                value: err,
                            });

                            // 📊 Google Analytics
                            trackEvent({
                                action: GA_EVENTS.EVENT_INTEREST_SUBMITTED,
                                parameters: {
                                    event_slug: eventslug,
                                    event_id: eventId,
                                    source: "event_home",
                                    source_page: window.location.pathname,
                                },
                            });
                            
                            showStatus(setStatus, {
                                type: "error",
                                message: "Unable to save your email right now.",
                            });
                            } finally {
                            setEventEmailSubmitting(false);
                            }
                        }}
                        >
                        <div className="input-group">
                            <input
                            type="email"
                            className="form-control"
                            placeholder="Email address"
                            value={eventEmail}
                            onChange={(e) => setEventEmail(e.target.value)}
                            required
                            />

                            <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={eventEmailSubmitting}
                            >
                            {eventEmailSubmitting
                                ? getCommunicationText(
                                    "stay_connected",
                                    "saving_button",
                                    "Saving..."
                                )
                                : getCommunicationText(
                                    "stay_connected",
                                    "join_button",
                                    "Join"
                                )}
                            </button>
                        </div>
                        </form>

                        <hr />

                        <div>
                        <div className="fw-bold small mb-2">
                        {getCommunicationText(
                            "stay_connected",
                            "share_heading",
                            "Share this event"
                        )}
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                            >
                            Facebook
                            </a>

                            <a
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                            >
                            LinkedIn
                            </a>

                            <a
                            href={`mailto:?subject=${shareText}&body=${encodedShareUrl}`}
                            className="btn btn-sm btn-outline-secondary"
                            >
                            Email
                            </a>
                        </div>
                        </div>

                    </div>
                    </div>
                </div>
                </div>
            </div>
            </div>

            {canManageEvent && (
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white">
                <button
                    type="button"
                    className="btn btn-link text-decoration-none text-reset text-start w-100 p-0 border-0"
                    onClick={() => setSetupProgressExpanded((current) => !current)}
                    aria-expanded={setupProgressExpanded}
                    aria-controls="event-setup-progress-details"
                >
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                        <div>
                        <strong>🧭 Event Setup Progress</strong>
                        <div className="small text-muted">
                            Manager/admin checklist for getting this public event page ready.
                        </div>
                        </div>

                        <div className="d-flex align-items-center gap-3 text-md-end">
                        <div>
                            <div className="fw-bold fs-4">{setupPercent}%</div>
                            <small className="text-muted">
                            {completedSetupItems} of {visibleSetupItems.length} complete
                            </small>
                        </div>

                        <span aria-hidden="true">
                            {setupProgressExpanded ? "▲" : "▼"}
                        </span>
                        </div>
                    </div>
                </button>
                </div>

                {setupProgressExpanded && (
                <div className="card-body" id="event-setup-progress-details">
                <div className="progress mb-4" style={{ height: "12px" }}>
                    <div
                    className={`progress-bar ${
                        setupPercent >= 80
                        ? "bg-success"
                        : setupPercent >= 50
                        ? "bg-warning"
                        : "bg-danger"
                    }`}
                    role="progressbar"
                    style={{ width: `${setupPercent}%` }}
                    aria-valuenow={setupPercent}
                    aria-valuemin="0"
                    aria-valuemax="100"
                    />
                </div>

                <div className="row g-4">
                    {visibleSetupItems.map((item) => (
                    <div key={item.label} className="col-12 col-md-6 col-xl-4">
                        <div className={`card h-100 border shadow-sm ${item.complete ? "" : "border-warning"}`}>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start gap-3">
                            <div>
                                <h5 className="mb-1">
                                {item.complete ? "✅" : "⚠"} {item.label}
                                </h5>

                                <p className="small text-muted mb-0">
                                {item.complete
                                    ? "Looks good."
                                    : "Needs setup before the event page feels complete."}
                                </p>
                            </div>

                            {!item.complete && (
                                <Link to={item.link} className="btn btn-warning btn-sm">
                                {item.button}
                                </Link>
                            )}
                            </div>
                        </div>
                        </div>
                    </div>
                    ))}
                </div>
                </div>
                )}
            </div>
            )}

            <div className="row g-4 mb-4">
            <div className="col-12 col-md-6 col-xl-3">
                <div className="card h-100 shadow-sm border-0">
                <div className="card-body">
                    <h5 className="fw-bold">🗳️ Voting</h5>

                    <p className="text-muted">
                    {isVotingOpen
                        ? "Voting is open. Review your ballot or continue voting."
                        : "Voting is not currently open."}
                    </p>

                    <Link
                    to={`/${eventslug}/ballot`}
                    className={`btn btn-sm ${isVotingOpen ? "btn-primary" : "btn-outline-secondary"}`}
                    >
                    ✅ My Ballot
                    </Link>
                </div>
                </div>
            </div>

            <div className="col-12 col-md-6 col-xl-3">
                <div className="card h-100 shadow-sm border-0">
                <div className="card-body">
                    <h5 className="fw-bold">✏️ Entries</h5>

                    <p className="text-muted">
                    {entryCount > 0
                        ? `${entryCount} entr${entryCount === 1 ? "y" : "ies"} listed for this event.`
                        : canManageEvent
                        ? "No entries have been added yet."
                        : "Entries are not listed yet."}
                    </p>

                    <Link
                    to={`/${eventslug}/entries`}
                    className={entryCount > 0 ? "btn btn-outline-primary btn-sm" : "btn btn-outline-secondary btn-sm"}
                    >
                    {canManageEvent && entryCount === 0 ? "Manage Entries" : "View Entries"}
                    </Link>
                </div>
                </div>
            </div>

            {!eventDayHasPassed && (volunteerActivityCount > 0 || canManageEvent) && (
                <div className="col-12 col-md-6 col-xl-3">
                <div className="card h-100 shadow-sm border-0">
                    <div className="card-body">
                    <h5 className="fw-bold">🙋 Volunteers</h5>

                    <p className="text-muted">
                        {volunteerActivityCount > 0
                        ? `${volunteerActivityCount} volunteer area${volunteerActivityCount === 1 ? "" : "s"} available.`
                        : "No volunteer opportunities have been added yet."}
                    </p>

                    <Link
                        to={`/${eventslug}/volunteer_opportunities`}
                        className={volunteerActivityCount > 0 ? "btn btn-outline-success btn-sm" : "btn btn-warning btn-sm"}
                    >
                        {volunteerActivityCount > 0 ? "Volunteer" : "Manage Volunteers"}
                    </Link>
                    </div>
                </div>
                </div>
            )}

            {sponsorsEnabled && (sponsors.length > 0 || canManageEvent) && (
                <div className="col-12 col-md-6 col-xl-3">
                <div className="card h-100 shadow-sm border-0">
                    <div className="card-body">
                    <h5 className="fw-bold">🌟 Sponsors</h5>

                    <p className="text-muted">
                        {sponsors.length > 0
                        ? `${sponsors.length} sponsor${sponsors.length === 1 ? "" : "s"} supporting this event.`
                        : "No sponsors have been added yet."}
                    </p>

                    <Link
                        to={sponsors.length > 0 ? `/${eventslug}/sponsors` : `/${eventslug}/sponsors_list`}
                        className={sponsors.length > 0 ? "btn btn-outline-dark btn-sm" : "btn btn-warning btn-sm"}
                    >
                        {sponsors.length > 0 ? "View Sponsors" : "Manage Sponsors"}
                    </Link>
                    </div>
                </div>
                </div>
            )}

            {auctionEnabled && (
                <div className="col-12 col-md-6 col-xl-3">
                <div className="card h-100 shadow-sm border-0">
                    <div className="card-body">
                    <h5 className="fw-bold">🏷️ Auction</h5>

                    <p className="text-muted">
                        {auctionItemCount > 0
                        ? `${auctionItemCount} auction item${auctionItemCount === 1 ? "" : "s"} available.`
                        : canManageEvent
                        ? "Auction is enabled, but no items have been added yet."
                        : "Auction items are not listed yet."}
                    </p>

                    <Link
                        to={auctionItemCount > 0 ? `/${eventslug}/auction` : `/${eventslug}/auctions`}
                        className={auctionItemCount > 0 ? "btn btn-outline-primary btn-sm" : "btn btn-warning btn-sm"}
                    >
                        {auctionItemCount > 0 ? "Browse Auction" : "Manage Auction"}
                    </Link>
                    </div>
                </div>
                </div>
            )}
            </div>

            {!user && (
            <div className="alert alert-success d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div>
                <strong>🚀 Get ready to vote!</strong>
                <br />
                Create your account now to participate in this event.
                </div>

                <div className="d-flex gap-2">
                <Link to="/register" className="btn btn-light btn-sm text-dark">
                    📝 Register
                </Link>

                <Link to="/login" className="btn btn-light btn-sm text-dark">
                    🔐 Login
                </Link>
                </div>
            </div>
            )}

            {isVotingOpen && (
            <div className="alert alert-info d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div>
                🗳️ <strong>Voting is open now!</strong>
                <br />
                Scan QR codes on entries to view and submit your vote.
                </div>

                <Link to={`/${eventslug}/ballot`} className="btn btn-primary btn-sm">
                ✅ Go to My Ballot
                </Link>
            </div>
            )}

            {orderedEventHomeSections.map((section) => (
                <div key={section.id} className="mt-4">
                    {section.render()}
                </div>
            ))}

            {feedbackNote && (
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white">
                <strong>📝 We’d Love Your Feedback!</strong>
                </div>

                <div className="card-body">
                {renderRichContent(feedbackNote)}

                <form
                    className="mt-4"
                    onSubmit={async (e) => {
                    e.preventDefault();

                    const form = e.target;
                    const name = form.name.value.trim();
                    const email = form.email.value.trim();
                    const message = form.message.value.trim();

                    if (!name || !email || !message) {
                        showStatus(setStatus, {
                        type: "error",
                        message: "All fields are required.",
                        });

                        return;
                    }

                    setSubmitting(true);

                    try {
                        await addDoc(collection(db, "events", eventId, "feedback"), {
                        name,
                        email,
                        message,
                        createdAt: serverTimestamp(),
                        });

                        // 📊 Google Analytics
                        trackEvent({
                            action: GA_EVENTS.CONTACT_FORM_SUBMITTED,
                            parameters: {
                                event_slug: eventslug,
                                event_id: eventId,
                                source: "event_feedback",
                                source_page: window.location.pathname,
                            },
                        });
                        
                        showStatus(setStatus, {
                        type: "success",
                        message: "Feedback submitted. Thank you!",
                        });

                        form.reset();
                    } catch (err) {
                        logger.error("❌ Error saving feedback:", {
                        value: err,
                        });

                        showStatus(setStatus, {
                        type: "error",
                        message: "Failed to submit feedback.",
                        });
                    } finally {
                        setSubmitting(false);
                    }
                    }}
                >
                    <div className="row g-3">
                    <div className="col-12 col-md-6">
                        <label htmlFor="name" className="form-label">
                        Your Name
                        </label>

                        <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        required
                        />
                    </div>

                    <div className="col-12 col-md-6">
                        <label htmlFor="email" className="form-label">
                        Your Email
                        </label>

                        <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        required
                        pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                        title="Enter a valid email address"
                        />
                    </div>

                    <div className="col-12">
                        <label htmlFor="message" className="form-label">
                        Your Comment
                        </label>

                        <textarea
                        id="message"
                        name="message"
                        rows="4"
                        className="form-control"
                        required
                        />
                    </div>

                    <div className="col-12">
                        <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={submitting}
                        >
                        {submitting ? "📤 Sending..." : "📤 Submit Feedback"}
                        </button>
                    </div>
                    </div>
                </form>
                </div>
            </div>
            )}
            
        </div>
        </section>
    </>
    );

};

export default EventHome;
